<script setup lang="ts">
import { useI18n } from 'vue-i18n'
const { t } = useI18n()
import type {
  IAoeCart,
  IAoeCartEarnedDiscount,
  IAoeCartGamification,
  IAoeCartGamificationAward,
  IAoeCartItem,
} from '@/types/CartTypes'
import { GetGamificationDifferenceMessage } from '@/services/CartService.ts'
import { ref, watch, onMounted, computed } from 'vue'
import { GetFullProductImageDictionary } from '@/services/SkuService.ts'
import AoeCartDetailSelectFreeProduct from './AoeCartDetailSelectFreeProduct.vue'
import AoeCartDetailFreeProduct from './AoeCartDetailFreeProduct.vue'
import AoeCartDetailSelectPercentageOffItem from './AoeCartDetailSelectPercentageOffItem.vue'
import AoeCartDetailDiscountedSkuFromList from './AoeCartDetailDiscountedSkuFromList.vue'
import AoeCartDetailCouponAward from './AoeCartDetailCouponAward.vue'
import AoeCartDetailFreeDollars from './AoeCartDetailFreeDollars.vue'
import AoeCartDetailDiscountedItem from './AoeCartDetailDiscountedItem.vue'
import { checkOutlineIcon } from '@progress/kendo-svg-icons'
import { SvgIcon } from '@progress/kendo-vue-common'
import { useLogger } from 'vue-logger-plugin'
const logger = useLogger()

const props = defineProps<{
  cart: IAoeCart
}>()

const emit = defineEmits(["free-gift-opted-out", "free-gift-selected", "show-free-gifts-again"]);


function onFreeGiftOptedOut() {
  emit("free-gift-opted-out");
}

function onFreeGiftSelected() {
  emit("free-gift-selected")
}

function showFreeGiftsAgain() {
  emit("show-free-gifts-again")
}

const earnedDiscounts = ref<Array<IAoeCartEarnedDiscount>>([])
const gamifications = ref<Array<IAoeCartGamification>>([])
const cartItemsByPoeDiscPlanLevelDiscIdDictionary = ref<{
  [id: string]: Array<IAoeCartItem>
}>({})

// Filter gamifications that should show progress bars (OrderTotal, PartyOrderTotal, or FreeShipping awards)
// Only show progress bars for gamifications that haven't been earned yet
const progressGamifications = computed(() => {
  return gamifications.value.filter(gm => {
    if (gm.requirements.length === 0) return false
    
    const isProgressType = gm.requirements[0].systemKeyword === 'OrderTotal' || 
                          gm.requirements[0].systemKeyword === 'PartyOrderTotal' || 
                          gm.awards.some(award => award.systemKeyword === 'FreeShipping')
    
    if (!isProgressType) return false
    
    // Check if this specific gamification has been earned by matching poeDiscPlanLevelDiscId
    // Each award now has a poeDiscPlanLevelDiscId that corresponds to the earned discount
    // This ensures that when there are multiple discounts with the same systemKeyword
    // and awardDescription (e.g., multiple FreeDollars at different thresholds),
    // only the specific earned one's progress bar disappears, not all of them
    const isEarned = gm.awards.some(award => 
      earnedDiscounts.value.some(ed => 
        ed.poeDiscPlanLevelDiscId === award.poeDiscPlanLevelDiscId
      )
    )
    
    // Only show progress bar if not yet earned
    return !isEarned
  })
})

onMounted(async () => {
  logger.debug('onMounted')
  if (props.cart && props.cart.earnedDiscounts) {
    earnedDiscounts.value = props.cart.earnedDiscounts
  }

  if (props.cart && props.cart.gamification) {
    gamifications.value = filterFreeShippingAwards(props.cart.gamification)
  }

  AfterCartUpdate()
})

const productImageUrls = ref<Record<string, string>>({})

watch(
  () => props.cart,
  newValue => {
    logger.debug('props.cart changed')
    if (newValue && newValue.earnedDiscounts) {
      earnedDiscounts.value = newValue.earnedDiscounts
    } else {
      earnedDiscounts.value = []
    }

    if (newValue && newValue.gamification) {
      gamifications.value = filterFreeShippingAwards(newValue.gamification)
    } else {
      gamifications.value = []
    }
    logger.debug('props.cart earnedDiscounts:', earnedDiscounts.value)
    AfterCartUpdate()
  },
)

function filterFreeShippingAwards(gamifications: IAoeCartGamification[]) : IAoeCartGamification[] {
  // DISABLED: Include all gamification awards including FreeShipping to enable progress bar display
  // Previously this function filtered out FreeShipping awards, preventing progress bar from showing
  return gamifications;
}

function AfterCartUpdate() {
  logger.debug('AfterCartUpdate')
  UpdateCartItemsByPoeDiscPlanLevelDiscIdDictionary()
  UpdateProductImageUrls()
}

async function UpdateProductImageUrls() {
  const uniqueProductCodes = Array.from(
    new Set(
      earnedDiscounts.value
        .map(earnedDiscount => earnedDiscount.items ?? [])
        .flat()
        .map(item => item.productCode),
    ),
  )
  for (const item of props.cart.items) {
    if (
      item.poeDiscPlanLevelDiscIds &&
      item.poeDiscPlanLevelDiscIds.length > 0
    ) {
      uniqueProductCodes.push(item.productCode)
    }
  }
  productImageUrls.value =
    await GetFullProductImageDictionary(uniqueProductCodes)
}

function GetFullProductImageUrlFromStringDictionary(
  productCode: string,
): string {
  logger.debug(
    'GetFullProductImageUrlFromStringDictionary',
    productCode,
    productImageUrls,
  )
  const productImageUrl = productImageUrls.value[productCode]
  logger.debug('productImageUrl=', productImageUrl)
  if (productImageUrl) return productImageUrl
  return '#'
}

function GetCartItemsByPoeDiscPlanLevelDiscId(
  poeDiscPlanLevelDiscId: number,
): IAoeCartItem[] {
  const poeDiscPlanLevelDiscIdKey = GetPoeDiscPlanLevelDiscIdKey(
    poeDiscPlanLevelDiscId,
  )
  const cartItems =
    cartItemsByPoeDiscPlanLevelDiscIdDictionary.value[poeDiscPlanLevelDiscIdKey]
  return cartItems
}

function GetPoeDiscPlanLevelDiscIdKey(poeDiscPlanLevelDiscId: number): string {
  return 'PoeDiscPlanLevelDiscId' + poeDiscPlanLevelDiscId
}

function UpdateCartItemsByPoeDiscPlanLevelDiscIdDictionary() {
  cartItemsByPoeDiscPlanLevelDiscIdDictionary.value = {}
  if (props.cart && props.cart.items) {
    props.cart.items.forEach(item => {
      if (item.poeDiscPlanLevelDiscIds) {
        item.poeDiscPlanLevelDiscIds.forEach(poeDiscPlanLevelDiscId => {
          const poeDiscPlanLevelDiscIdKey = GetPoeDiscPlanLevelDiscIdKey(
            poeDiscPlanLevelDiscId,
          )
          if (
            !cartItemsByPoeDiscPlanLevelDiscIdDictionary.value[
              poeDiscPlanLevelDiscIdKey
            ]
          ) {
            cartItemsByPoeDiscPlanLevelDiscIdDictionary.value[
              poeDiscPlanLevelDiscIdKey
            ] = []
          }
          cartItemsByPoeDiscPlanLevelDiscIdDictionary.value[
            poeDiscPlanLevelDiscIdKey
          ].push(item)
        })
      }
    })
  }
}

function FindCartItems(poeDiscPlanLevelDiscId: number): IAoeCartItem[] {
  const poeDiscPlanLevelDiscIdKey = GetPoeDiscPlanLevelDiscIdKey(
    poeDiscPlanLevelDiscId,
  )
  const cartItems =
    cartItemsByPoeDiscPlanLevelDiscIdDictionary.value[poeDiscPlanLevelDiscIdKey]

  if (cartItems == null) return []

  return cartItems
}

function FindCartItem(
  poeDiscPlanLevelDiscId: number,
  skuCode: string,
): IAoeCartItem | null {
  const cartItems = FindCartItems(poeDiscPlanLevelDiscId)
  if (cartItems) {
    const cartItem = cartItems.find(item => item.skuCode === skuCode)
    if (cartItem) return cartItem
  }
  return null
}
</script>
<template>
  <div>
    <div v-if="progressGamifications.length > 0">
      <div class="row" v-for="gm in progressGamifications" :key="gm.differenceMessage">
          <div class="row gamificationDiv">
            <div
              class="gamificationMessage col-12"
              v-html="GetGamificationDifferenceMessage(t, gm)"
            ></div>
          </div>
          <div class="row gamificationSliderDiv">
            <div class="col-12">
              <input
                class="slider gamificationRange"
                disabled
                type="range"
                min="0"
                :max="gm.requirements[0].totalAmount"
                :value="
                  gm.requirements[0].totalAmount -
                  gm.requirements[0].difference
                "
              />
            </div>
          </div>
      </div>
    </div>

    <div
      class="aoe-cart-detail-discount pb-2 pt-2"
      v-if="earnedDiscounts.length > 0"
    >
      <div
        class="row mt-3"
        v-for="(earnedDiscount, index) in earnedDiscounts"
        :key="earnedDiscount.poeDiscPlanLevelDiscId"
      >
        <template v-if="earnedDiscount.systemKeyword === 'SelectFreeProduct'">
          <AoeCartDetailSelectFreeProduct
            :cart="props.cart"
            :earnedDiscount="earnedDiscount"
            :GetCartItemsByPoeDiscPlanLevelDiscId="
              GetCartItemsByPoeDiscPlanLevelDiscId
            "
            :FindCartItem="FindCartItem"
            :GetFullProductImageUrlFromStringDictionary="
              GetFullProductImageUrlFromStringDictionary
            "
            @free-gift-opted-out="onFreeGiftOptedOut"
            @free-gift-selected="onFreeGiftSelected"
            @show-free-gifts-again="showFreeGiftsAgain"
          ></AoeCartDetailSelectFreeProduct>
        </template>
        <template v-else-if="earnedDiscount.systemKeyword === 'FreeProduct'">
          <div class="col-12 free-product-discount">
            <AoeCartDetailFreeProduct
              :earnedDiscount="earnedDiscount"
              :FindCartItems="FindCartItems"
              :GetFullProductImageUrlFromStringDictionary="
                GetFullProductImageUrlFromStringDictionary
              "
            ></AoeCartDetailFreeProduct>
          </div>
        </template>
        <template v-else-if="earnedDiscount.systemKeyword === 'FreeShipping'">
          <div class="col-1">
            <SvgIcon class="check-mark-outline-icon" :icon="checkOutlineIcon">
            </SvgIcon>
          </div>
          <div class="col-9 free-shipping-award-desc">
            {{ earnedDiscount.awardDescription }}
          </div>
        </template>
        <template
          v-else-if="earnedDiscount.systemKeyword === 'PercentageOffOrder'"
        >
          <div class="col-1">
            <SvgIcon class="check-mark-outline-icon" :icon="checkOutlineIcon">
            </SvgIcon>
          </div>
          <div class="col-9 percent-off-order-award-desc">
            {{ earnedDiscount.awardMessage }}&nbsp;{{
              earnedDiscount.awardDescription
            }}
          </div>
        </template>
        <template
          v-else-if="earnedDiscount.systemKeyword === 'PercentageOffItem'"
        >
          <AoeCartDetailSelectPercentageOffItem
            :cart="props.cart"
            :earnedDiscount="earnedDiscount"
            :GetCartItemsByPoeDiscPlanLevelDiscId="
              GetCartItemsByPoeDiscPlanLevelDiscId
            "
            :FindCartItems="FindCartItems"
            :GetFullProductImageUrlFromStringDictionary="
              GetFullProductImageUrlFromStringDictionary
            "
          >
          </AoeCartDetailSelectPercentageOffItem>
        </template>
        <template v-else-if="earnedDiscount.systemKeyword === 'Coupon'">
          <AoeCartDetailCouponAward :earnedDiscount="earnedDiscount">
          </AoeCartDetailCouponAward>
        </template>
        <template v-else-if="earnedDiscount.systemKeyword === 'FreeDollars'">
          <AoeCartDetailFreeDollars
            :cart="props.cart"
            :earnedDiscount="earnedDiscount"
            :FindCartItems="FindCartItems"
            :GetFullProductImageUrlFromStringDictionary="
              GetFullProductImageUrlFromStringDictionary
            "
          >
          </AoeCartDetailFreeDollars>
        </template>
        <template v-else-if="earnedDiscount.systemKeyword === 'DiscountedItem'">
          <div class="col-12 discounted-item-discount">
            <AoeCartDetailDiscountedItem
              :cart="props.cart"
              :earnedDiscount="earnedDiscount"
              :FindCartItems="FindCartItems"
              :GetFullProductImageUrlFromStringDictionary="
                GetFullProductImageUrlFromStringDictionary
              "
            ></AoeCartDetailDiscountedItem>
          </div>
        </template>
        <template
          v-else-if="earnedDiscount.systemKeyword === 'DiscountedSkuFromList'"
        >
          <AoeCartDetailDiscountedSkuFromList
            :cart="props.cart"
            :earnedDiscount="earnedDiscount"
            :GetCartItemsByPoeDiscPlanLevelDiscId="
              GetCartItemsByPoeDiscPlanLevelDiscId
            "
            :FindCartItems="FindCartItems"
            :GetFullProductImageUrlFromStringDictionary="
              GetFullProductImageUrlFromStringDictionary
            "
          >
          </AoeCartDetailDiscountedSkuFromList>
        </template>
        <div class="col-12" v-else>
          {{ t('cart.SystemKeyword') }} {{ earnedDiscount.systemKeyword }} {{ t('cart.NotSupported') }}
        </div>
        
        <!-- Divider between discount awards -->
        <div v-if="index < earnedDiscounts.length - 1" class="col-12 mt-3">
          <hr class="discount-divider" />
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.aoe-cart-detail-discount {
  border-bottom: 1px solid #e0e0e0;

  .discount-divider {
    border: 0;
    border-top: 1px solid #e0e0e0;
    margin: 0;
  }

  .unselect-select-free-product {
    text-align: right;
  }

  .free-product-discount {
    padding: $spacing-2-5;
  }

  .check-mark-outline-icon {
    width: 24px;
    height: 24px;
    color: var(--standardColor);
  }


  .free-item-message {
    align-content: center;
  }

  .select-free-product {
    cursor: pointer;
    text-align: center;
    padding: $spacing-1;
    @include typography-body;
    @include font-weight-regular;
    font-style: normal;
  }

  .prodImage {
    max-height: 50px;
    max-width: 50px;
    margin: auto;
  }

  .selectedLabelDiv {
    text-align: left;
  }

  .checkMark {
    color: var(--standardColor) !important;
  }

  .freeItemOptions {
    display: table;
    border-spacing: 20px;
    margin-top: -20px;
    margin-bottom: -20px;
    margin-left: auto;
    margin-right: auto;
  }

  .freeOption {
    display: table-cell;
  }

  .freeOption.selected {
    border-color: var(--standardColor) !important;
  }
  .discountMessage {
    @include typography-h4;
    @include font-weight-medium;
    font-style: normal;
  }

  .percent-off-item-entry-div .single-line-auto-complete-row
  {
    margin-top: 0rem !important;
  }

  .percent-off-item-entry-div .single-line-auto-complete-div
  {
    line-height: 34px;
    align-content: center;
  }

  .percent-off-item-entry-div .single-line-add-to-cart-button
  {
    line-height: 34px;
    align-content: center;
    padding: 0 $spacing-4 !important;
  }

  .unselect-select-free-product {
    @include typography-small;
    @include font-weight-medium;
    font-style: normal;
  }

  .unselect-select-free-product .k-button-text {
    color: var(--buttonPrimaryColor);
    text-wrap-mode: wrap;
  }

  .unselect-select-free-product .k-button-link {
    text-decoration: underline;
  }

  .notSelected {
    color: white;
  }

  @media screen and (max-width: 768px) {
    .freeItemOptions {
      display: table;
      border-spacing: 10px;
      margin-top: -10px;
      margin-bottom: 10px;
      margin-left: auto;
      margin-right: auto;
    }
    .selectedLabelDiv {
      text-align: left;
      @include typography-caption;
      margin-bottom: -5px;
    }
  }

  .percent-off-order-award-desc,
  .free-shipping-award-desc {
    @include typography-h4;
  }

  .check-mark-outline-icon
  {
    width: 24px;
    height: 24px;
    color: var(--standardColor);
  }
}

.gamificationMessage {
  @include typography-body-large;
}

.gamificationRange {
  width: 100% !important;
  background: #f0f0f0 !important;
}
@media screen and (-webkit-min-device-pixel-ratio: 0) {
  input[type='range'] {
    overflow: hidden;
    width: 80px;
    background-color: #9a905d;
  }

  input[type='range']::-webkit-slider-thumb {
    width: 10px;
    -webkit-appearance: none;
    height: 10px;
    cursor: ew-resize;
    background: #b4023f;
    box-shadow: -1000px 0 0 1000px #b4023f;
  }
}
</style>
